/* eslint-disable @typescript-eslint/naming-convention */
var BrowserType;
(function (BrowserType) {
    BrowserType["IE"] = "IE";
    BrowserType["Edge"] = "Edge";
    BrowserType["EdgeLegacy"] = "Edge Legacy";
    BrowserType["Chrome"] = "Chrome";
    BrowserType["Firefox"] = "Firefox";
    BrowserType["Safari"] = "Safari";
    BrowserType["PhantomJS"] = " PhantomJS";
    BrowserType["Opera"] = "Opera";
    BrowserType["WebView"] = "WebKit";
    BrowserType["Other"] = "Other";
})(BrowserType || (BrowserType = {}));
const browsers = [
    { keyword: 'PhantomJS/', browserType: BrowserType.PhantomJS },
    { keyword: 'Edge/', browserType: BrowserType.EdgeLegacy },
    { keyword: 'EdgA/', browserType: BrowserType.Edge },
    { keyword: 'EdgiOS/', browserType: BrowserType.Edge },
    { keyword: 'Edg/', browserType: BrowserType.Edge },
    { keyword: 'wv/', browserType: BrowserType.WebView },
    { keyword: 'MSIE/', browserType: BrowserType.IE },
    { keyword: 'Trident/', browserType: BrowserType.IE },
    { keyword: 'OPR/', browserType: BrowserType.Opera },
    { keyword: 'Chrome/', browserType: BrowserType.Chrome },
    { keyword: 'CriOS/', browserType: BrowserType.Chrome },
    { keyword: 'Firefox/', browserType: BrowserType.Firefox },
    { keyword: 'Safari/', browserType: BrowserType.Safari },
];
var PlatformType;
(function (PlatformType) {
    PlatformType[PlatformType["PC"] = 0] = "PC";
    PlatformType[PlatformType["Mac"] = 1] = "Mac";
    PlatformType[PlatformType["WindowsPhone"] = 2] = "WindowsPhone";
    PlatformType[PlatformType["iPad"] = 3] = "iPad";
    PlatformType[PlatformType["iPhone"] = 4] = "iPhone";
    PlatformType[PlatformType["AndroidPhone"] = 5] = "AndroidPhone";
    PlatformType[PlatformType["AndroidTablet"] = 6] = "AndroidTablet";
    PlatformType[PlatformType["Linux"] = 7] = "Linux";
    PlatformType[PlatformType["Chromebook"] = 8] = "Chromebook";
    PlatformType[PlatformType["Other"] = 9] = "Other";
})(PlatformType || (PlatformType = {}));
const platforms = [
    { keyword: 'Windows NT', platformType: PlatformType.PC },
    { keyword: 'Windows XP', platformType: PlatformType.PC },
    { keyword: 'Macintosh', platformType: PlatformType.Mac },
    { keyword: 'Windows Phone', platformType: PlatformType.WindowsPhone },
    { keyword: 'iPad', platformType: PlatformType.iPad },
    { keyword: 'iPhone', platformType: PlatformType.iPhone },
    { keyword: 'Android', platformType: PlatformType.AndroidPhone },
    { keyword: 'Linux', platformType: PlatformType.Linux },
    { keyword: 'CrOS', platformType: PlatformType.Chromebook },
];
/**
 * Get the device's info.
 *
 * @returns DeviceInfo containing the device's Platform and Browser info.
 */
export function getDeviceInfo() {
    const browserInfo = getBrowserInfo();
    const osInfo = getOsInfo();
    const device = { browser: browserInfo, os: osInfo };
    return device;
}
function getBrowserInfo() {
    const userAgent = navigator.userAgent;
    let browser = { name: 'Other', version: 'v1' };
    browsers.some((value) => {
        if (userAgent.includes(value.keyword)) {
            const name = value.browserType;
            const version = userAgent.split(value.keyword)[1].split(' ')[0];
            browser = { name, version };
            return true;
        }
        return false;
    });
    return browser;
}
function getOsInfo() {
    const userAgent = navigator.userAgent;
    const loweredUserAgentString = userAgent.toLowerCase();
    let platform = { keyword: 'Other', platformType: PlatformType.Other };
    platforms.some((value) => {
        if (loweredUserAgentString.includes(value.keyword.toLowerCase())) {
            platform = value;
            return true;
        }
        return false;
    });
    // Can't use keyword alone to distinguish between Android Phone and Android tablet
    if (platform.platformType === PlatformType.AndroidPhone && loweredUserAgentString.indexOf('mobile') === -1) {
        platform = { keyword: 'Android', platformType: PlatformType.AndroidTablet };
    }
    let version = '0.0.0';
    if (platform.platformType === PlatformType.Mac) {
        // This is to extract the Mac OS version, e.g.
        // Mozilla/5.0 (Macintosh; Intel Mac OS X 10_10_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/41.0.2227.1 Safari/537.36
        // Mozilla/5.0 (Macintosh; Intel Mac OS X 10.6; rv:25.0) Gecko/20100101 Firefox/25.0
        const match = /Mac OS X (\d+)(_|\.)(\d+)([_.]?)(\d*)/i.exec(userAgent);
        if (match && match.length >= 6) {
            version = `${match[1]}.${match[3]}.${match[5]}`;
        }
    }
    else if (platform.platformType === PlatformType.PC || platform.platformType === PlatformType.WindowsPhone) {
        if (loweredUserAgentString.indexOf('windows xp') !== -1) {
            // XP shows up as either just xp or nt 5.1
            version = '5.1.0';
        }
        else {
            // This is to extract the Windows version, e.g.
            // Mozilla/5.0 (Windows NT 10.0; <64-bit tags>) AppleWebKit/<WebKit Rev> (KHTML, like Gecko) Chrome/<Chrome Rev> Safari/<WebKit Rev> Edge/<EdgeHTML Rev>.<Windows Build>
            // Mozilla/5.0 (Windows Phone 10.0; Android <Android Version>; <Device Manufacturer>; <Device Model>) AppleWebKit/<WebKit Rev> (KHTML, like Gecko) Chrome/<Chrome Rev> Mobile Safari/<WebKit Rev> Edge/<EdgeHTML Rev>.<Windows Build>
            const match = /Windows (NT|Phone) (OS )?(\d+)\.(\d+)/i.exec(userAgent);
            if (match && match.length >= 5) {
                version = `${match[3]}.${match[4]}.0`;
            }
        }
    }
    else if (platform.platformType === PlatformType.iPad || platform.platformType === PlatformType.iPhone) {
        const match = loweredUserAgentString.match(/os (\d+)_(\d+)(_(\d+))?/);
        if (match) {
            const major = match[1] || 0;
            const minor = match[2] || 0;
            const buildPart = match[4] || 0;
            version = `${major}.${minor}.${buildPart}`;
        }
    }
    else if (platform.platformType === PlatformType.AndroidPhone ||
        platform.platformType === PlatformType.AndroidTablet) {
        const match = loweredUserAgentString.match(/Android (\d+).(\d+)(.(\d+))?/);
        if (match) {
            const major = match[1] || 0;
            const minor = match[2] || 0;
            const buildPart = match[4] || 0;
            version = `${major}.${minor}.${buildPart}`;
        }
    }
    const osInfo = { name: platform.keyword, version };
    return osInfo;
}
//# sourceMappingURL=deviceDetector.js.map